import Reveal from "@/components/Reveal";
import VocabTag from "@/components/VocabTag";

const TILES = [
  {
    k: "01",
    title: "Domain-specific, not generalist.",
    body: "An AGI-native platform purpose-built for pharmaceutical manufacturing — intelligence and production owned as one.",
  },
  {
    k: "02",
    title: "Physics-bounded. Zero-hallucination.",
    body: "Outputs are binary: in-spec or out-of-spec. Decisions bounded by the validated state of each unit operation.",
  },
  {
    k: "03",
    title: "AGI integrated with a production-ready manufacturing body.",
    body: "Intelligence and industrial execution owned as one platform. Not software pointed at a factory — a unified operator of cognition and capacity.",
  },
  {
    k: "04",
    title: "Strategic infrastructure.",
    body: "Once installed, Giga One becomes the substrate on which national pharmaceutical capacity is produced. Switching costs in years.",
  },
];

/**
 * Why Giga One — opens with a typographic POSTER intro (SOVEREIGN / SYNTHESIS)
 * then transitions into a strict 2×2 bento grid.
 */
export default function WhyGigaOne() {
  return (
    <section
      id="differentiation"
      data-testid="why-giga-one-section"
      className="border-b border-[color:var(--g1-rule)]"
    >
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 lg:px-14 pt-24 md:pt-32 lg:pt-40 pb-24 md:pb-32">
        <div className="grid grid-cols-12 gap-8 mb-10 md:mb-14">
          <div className="col-span-6 md:col-span-3 lg:col-span-2">
            <Reveal>
              <div className="g1-label">SEC. 05</div>
              <div className="g1-eyebrow mt-2">Difference</div>
            </Reveal>
          </div>
          <div className="col-span-6 md:col-span-3 lg:col-span-2 md:col-start-10 lg:col-start-11 text-right flex flex-col items-end gap-2">
            <Reveal>
              <VocabTag kind="FUSED" />
            </Reveal>
          </div>
        </div>

        {/* Poster 2 — Chivo / wide tracking */}
        <Reveal delay={80}>
          <div className="border-t border-b border-[color:var(--g1-ink)] py-10 md:py-16 mb-14 md:mb-20">
            <h2
              data-testid="why-giga-one-heading"
              className="font-sans font-light uppercase text-[38px] sm:text-[56px] md:text-[88px] lg:text-[112px] xl:text-[128px] leading-[0.95] tracking-[-0.01em] text-[color:var(--g1-ink)]"
              style={{ letterSpacing: "-0.01em" }}
            >
              Sovereign <br />
              <span className="italic font-serif normal-case text-[color:var(--g1-olive)] tracking-tight">
                synthesis.
              </span>
            </h2>
          </div>
        </Reveal>

        <Reveal delay={140}>
          <p className="g1-lede max-w-[58ch] mb-14 md:mb-20">
            Not automation. Not generalist AI.{" "}
            <span className="text-[color:var(--g1-ink)]">
              Industrial AGI, purpose-built
            </span>{" "}
            for the category that matters most.
          </p>
        </Reveal>

        {/* Bento */}
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[color:var(--g1-rule)]">
          {TILES.map((t, i) => (
            <Reveal
              key={t.k}
              delay={i * 80}
              className="group border-r border-b border-[color:var(--g1-rule)] p-8 md:p-10 lg:p-14 hover:bg-[color:var(--g1-paper)]/60 transition-colors duration-500"
            >
              <div className="flex items-start justify-between mb-8">
                <span className="g1-label text-[color:var(--g1-ink-3)]">
                  0{t.k.slice(1)}
                </span>
                <span
                  aria-hidden
                  className="text-[color:var(--g1-ink-3)] group-hover:text-[color:var(--g1-olive)] transition-colors"
                >
                  →
                </span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl lg:text-[36px] leading-snug text-[color:var(--g1-ink)] max-w-[22ch]">
                {t.title}
              </h3>
              <p className="font-sans text-[15px] leading-relaxed text-[color:var(--g1-ink-2)] mt-5 max-w-[36ch]">
                {t.body}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
